/**
 * Analytics API Slice
 * Injects analytics/parking endpoints into the base API slice
 */

import { apiSlice } from "../api/apiSlice";
import {
  DailyAnalytics,
  MonthlyAnalytics,
  YearlyAnalytics,
  AnalyticsResponse,
  ParkingAnalytics,
} from "../../types";
import { DateFilterParams } from "../messages/messagesApiSlice";
import { generateListCacheTag, CACHE_TIME } from "../api/apiHelpers";

export const analyticsApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Get daily analytics for a venue
     * venueId is extracted from JWT token by backend
     */
    getDailyAnalytics: builder.query<
      AnalyticsResponse<DailyAnalytics>,
      { startDate: string; endDate: string }
    >({
      query: ({ startDate, endDate }) => ({
        url: `/api/analytics/daily`,
        params: { startDate, endDate },
      }),
      providesTags: (result, error, { startDate, endDate }) => [
        { type: "Analytics", id: "DAILY" },
        {
          type: "Analytics",
          id: generateListCacheTag("DAILY", startDate, endDate),
        },
      ],
      keepUnusedDataFor: CACHE_TIME.MEDIUM,
    }),

    /**
     * Get monthly analytics for a venue
     * venueId is extracted from JWT token by backend
     */
    getMonthlyAnalytics: builder.query<
      AnalyticsResponse<MonthlyAnalytics>,
      { startDate: string; endDate: string }
    >({
      query: ({ startDate, endDate }) => ({
        url: `/api/analytics/monthly`,
        params: { startDate, endDate },
      }),
      providesTags: (result, error, { startDate, endDate }) => [
        { type: "Analytics", id: "MONTHLY" },
        {
          type: "Analytics",
          id: generateListCacheTag("MONTHLY", startDate, endDate),
        },
      ],
      keepUnusedDataFor: CACHE_TIME.MEDIUM,
    }),

    /**
     * Get yearly analytics for a venue
     * venueId is extracted from JWT token by backend
     */
    getYearlyAnalytics: builder.query<
      AnalyticsResponse<YearlyAnalytics>,
      { startDate: string; endDate: string }
    >({
      query: ({ startDate, endDate }) => ({
        url: `/api/analytics/yearly`,
        params: { startDate, endDate },
      }),
      providesTags: (result, error, { startDate, endDate }) => [
        { type: "Analytics", id: "YEARLY" },
        {
          type: "Analytics",
          id: generateListCacheTag("YEARLY", startDate, endDate),
        },
      ],
      keepUnusedDataFor: CACHE_TIME.MEDIUM,
    }),

    /**
     * Get dashboard summary data
     * Not used yet
     */
    getDashboardSummary: builder.query<
      {
        success: boolean;
        data: {
          totalTickets: number;
          totalRevenue: number;
          totalMessages: number;
          totalPayments: number;
        };
      },
      DateFilterParams | void
    >({
      query: (params) => ({
        url: "/api/analytics/summary",
        params: params ? params : undefined,
      }),
      providesTags: [{ type: "Analytics", id: "SUMMARY" }],
    }),

    /**
     * Get revenue trends
     * Not used yet
     */
    getRevenueTrends: builder.query<
      {
        success: boolean;
        data: Array<{
          date: string;
          revenue: number;
          transactions: number;
        }>;
      },
      DateFilterParams | void
    >({
      query: (params) => ({
        url: "/api/analytics/revenue-trends",
        params: params ? params : undefined,
      }),
      providesTags: [{ type: "Analytics", id: "TRENDS" }],
    }),

    /**
     * Get parking analytics with optional date filtering (Legacy endpoint)
     */
    getParkingAnalytics: builder.query<
      { success: boolean; data: ParkingAnalytics },
      DateFilterParams | void
    >({
      query: (params) => ({
        url: "/api/analytics",
        params: params ? params : undefined,
      }),
      providesTags: [{ type: "Analytics", id: "PARKING" }],
      keepUnusedDataFor: 60,
    }),
  }),
});

// Export hooks for use in components
export const {
  useGetDailyAnalyticsQuery,
  useGetMonthlyAnalyticsQuery,
  useGetYearlyAnalyticsQuery,
  useLazyGetDailyAnalyticsQuery,
  useLazyGetMonthlyAnalyticsQuery,
  useLazyGetYearlyAnalyticsQuery,
  // useGetDashboardSummaryQuery,
  // useGetRevenueTrendsQuery,
  // useGetParkingAnalyticsQuery,
  // useLazyGetParkingAnalyticsQuery,
} = analyticsApiSlice;
